import { LOG_OUT } from "../../../auth/actions/actions-types";
import { Status } from "../../../models/gameplan";
import { ActionTypes, type ActionReturns } from "./data.actions";
import { initialState, type State } from "./data.state";

export const defaultReducer = (
  state: State = initialState,
  action: ActionReturns
) => {
  switch (action.type) {
    case ActionTypes.DATA_SET_LEARNING_PATH: {
      const learningPath = action.payload.learningPath;
      const stateLearningPath = state.learningPath[learningPath.id];

      if (stateLearningPath) {
        return {
          ...state,
          learningPath: {
            ...state.learningPath,
            [learningPath.id]: {
              ...stateLearningPath,
              ...learningPath,
            },
          },
        };
      }

      return {
        ...state,
        learningPath: {
          ...state.learningPath,
          [learningPath.id]: learningPath,
        },
      };
    }
    case ActionTypes.DATA_SET_COURSES: {
      const stateCourses = { ...state.course };
      const courses = action.payload.courses;

      for (const course of courses) {
        const courseId = course.id;
        if (stateCourses[courseId]) {
          stateCourses[courseId] = {
            ...stateCourses[courseId],
            ...course,
          };
        } else {
          stateCourses[courseId] = course;
        }
      }

      return { ...state, course: stateCourses };
    }
    case ActionTypes.DATA_SET_COURSE: {
      const course = action.payload.course;
      const stateCourse = state.course[course.id];

      if (stateCourse) {
        return {
          ...state,
          course: {
            ...state.course,
            [course.id]: {
              ...stateCourse,
              ...course,
            },
          },
        };
      }

      return {
        ...state,
        course: {
          ...state.course,
          [course.id]: course,
        },
      };
    }
    case ActionTypes.DATA_SET_COURSE_STATUS_BY_ID: {
      const { courseId, status } = action.payload;
      const stateCourse = state.course[courseId];

      return {
        ...state,
        course: {
          ...state.course,
          [courseId]: {
            ...stateCourse,
            status,
          },
        },
      };
    }
    case ActionTypes.DATA_SET_COURSE_COMMITMENT_STATUS_BY_ID: {
      const { courseId, status } = action.payload;
      const stateCourse = state.course[courseId];

      return {
        ...state,
        course: {
          ...state.course,
          [courseId]: {
            ...stateCourse,
            commitmentStatus: status,
          },
        },
      };
    }
    case ActionTypes.DATA_SET_MODULES: {
      const stateModules = { ...state.module };
      const modules = action.payload.modules;

      for (const module of modules) {
        const moduleId = module.id;
        if (stateModules[moduleId]) {
          stateModules[moduleId] = {
            ...stateModules[moduleId],
            ...module,
          };
        } else {
          stateModules[moduleId] = module;
        }
      }

      return { ...state, module: stateModules };
    }
    case ActionTypes.DATA_SET_MODULE: {
      const module = action.payload.module;
      const stateModule = state.module[module.id];

      if (stateModule) {
        return {
          ...state,
          module: {
            ...state.module,
            [module.id]: {
              ...stateModule,
              ...module,
            },
          },
        };
      }

      return {
        ...state,
        module: {
          ...state.module,
          [module.id]: module,
        },
      };
    }
    case ActionTypes.DATA_SET_MODULE_STATUS_BY_ID: {
      const { moduleId, status } = action.payload;
      const stateModule = state.module[moduleId];

      if (
        stateModule?.status === Status.COMPLETED ||
        (stateModule?.status === Status.IN_PROGRESS && status === Status.NEW)
      ) {
        return state;
      }

      return {
        ...state,
        module: {
          ...state.module,
          [moduleId]: {
            ...stateModule,
            status,
          },
        },
      };
    }
    case ActionTypes.DATA_SET_LESSONS: {
      const stateLessons = { ...state.lesson };
      const lessons = action.payload.lessons;

      for (const lesson of lessons) {
        const lessonId = lesson.id;
        if (stateLessons[lessonId]) {
          stateLessons[lessonId] = {
            ...stateLessons[lessonId],
            ...lesson,
          };
        } else {
          stateLessons[lessonId] = lesson;
        }
      }

      return { ...state, lesson: stateLessons };
    }
    case ActionTypes.DATA_SET_LESSON: {
      const lesson = action.payload.lesson;
      const stateLesson = state.lesson[lesson.id];

      if (stateLesson) {
        return {
          ...state,
          lesson: {
            ...state.lesson,
            [lesson.id]: {
              ...stateLesson,
              ...lesson,
            },
          },
        };
      }

      return {
        ...state,
        lesson: {
          ...state.lesson,
          [lesson.id]: lesson,
        },
      };
    }
    case ActionTypes.DATA_SET_LESSON_STATUS_BY_ID: {
      const { lessonId, status } = action.payload;
      const stateLesson = state.lesson[lessonId];

      return {
        ...state,
        lesson: {
          ...state.lesson,
          [lessonId]: {
            ...stateLesson,
            status,
          },
        },
      };
    }
    case ActionTypes.DATA_SET_WORKOUTS: {
      const stateWorkouts = { ...state.workout };
      const workouts = action.payload.workouts;

      for (const workout of workouts) {
        const workoutId = workout.id;
        if (stateWorkouts[workoutId]) {
          stateWorkouts[workoutId] = {
            ...stateWorkouts[workoutId],
            ...workout,
          };
        } else {
          stateWorkouts[workoutId] = workout;
        }
      }

      return { ...state, workout: stateWorkouts };
    }
    case ActionTypes.DATA_SET_WORKOUT: {
      const workout = action.payload.workout;
      const stateWorkout = state.workout[workout.id];

      if (stateWorkout) {
        return {
          ...state,
          workout: {
            ...state.workout,
            [workout.id]: {
              ...stateWorkout,
              ...workout,
            },
          },
        };
      }

      return {
        ...state,
        workout: {
          ...state.workout,
          [workout.id]: workout,
        },
      };
    }
    case ActionTypes.DATA_SET_WORKOUT_STATUS_BY_ID: {
      const { workoutId, status } = action.payload;
      const stateWorkout = state.workout[workoutId];

      return {
        ...state,
        workout: {
          ...state.workout,
          [workoutId]: {
            ...stateWorkout,
            status,
          },
        },
      };
    }
    case ActionTypes.DATA_SET_SLIDES: {
      const stateSlides = { ...state.slide };
      const slides = action.payload.slides;

      for (const slide of slides) {
        const slideId = slide.id;
        if (stateSlides[slideId]) {
          stateSlides[slideId] = {
            ...stateSlides[slideId],
            ...slide,
          };
        } else {
          stateSlides[slideId] = slide;
        }
      }

      return { ...state, slide: stateSlides };
    }
    case LOG_OUT: {
      return initialState;
    }
    default: {
      return state;
    }
  }
};
